import { useEffect, useState } from "react";
import { api } from "../../api/client";
import type { Stats } from "../../api/types";
import { admin, type Anomaly, type Run } from "../../api/admin";
import { Counters, StateBadge, took, when, StopRun } from "./bits";

export function Dashboard({ onWatch }: { onWatch: (id: number) => void }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.stats().then((s) => alive && setStats(s)).catch((e: Error) => alive && setError(e.message));
      admin.runs({ limit: 8 }).then((r) => alive && setRuns(r)).catch(() => { /* shown elsewhere */ });
    };
    load();
    admin.anomalies().then((a) => alive && setAnomalies(a)).catch(() => { /* not fatal */ });
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (error) return <div className="error">{error}</div>;
  if (!stats) return <div className="loading"><span className="spinner" />loading…</div>;

  const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);

  return (
    <>
      <section className="panel-section">
        <h2>The catalogue</h2>
        <div className="tiles">
          <Tile label="releases" value={stats.releases} />
          <Tile label="on Bandcamp" value={stats.matched}
                sub={`${pct(stats.matched, stats.releases)}% of the catalogue`} />
          <Tile label="not found" value={stats.notFound} />
          <Tile label="in more than one shop" value={stats.multiShop} />
          <Tile label="shop listings" value={stats.sources} />
          <Tile label="tracks" value={stats.tracks} />
        </div>
      </section>

      <section className="panel-section">
        <h2>How far each stage has got</h2>
        <div className="tiles">
          <Tile label="carry Bandcamp tags" value={stats.tagged}
                sub={`${pct(stats.tagged, stats.matched)}% of matched`} />
          <Tile label="tracks with a measured BPM" value={stats.bpmMeasured}
                sub={`${pct(stats.bpmMeasured, stats.tracks)}% of tracks`} />
          <Tile label="looked at, no steady pulse" value={stats.bpmUnknown} />
          <Tile label="nobody has looked yet" value={stats.bpmUnchecked}
                sub="what bpm still has to do" />
          <Tile label="dated from Discogs" value={stats.datedByDiscogs} />
          <Tile label="no date anywhere" value={stats.undated} />
          <Tile label="preview URLs cached" value={stats.streamsCachedFor}
                sub="warmed by the streams job" />
          <Tile label="distinct Bandcamp tags" value={stats.distinctBcTags} />
        </div>
      </section>

      <section className="panel-section">
        <h2>The shops</h2>
        <table className="runs">
          <thead><tr><th>shop</th><th>listings</th><th>only there</th></tr></thead>
          <tbody>
            {stats.shops?.map((s) => (
              <tr key={s.site}>
                <td>{s.label} <code>{s.site}</code></td>
                <td className="num">{s.listings.toLocaleString()}</td>
                <td className="num dim">
                  {s.exclusive.toLocaleString()} ({pct(s.exclusive, s.listings)}%)
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel-section">
        <h2>Recent runs</h2>
        {runs.length === 0 && <p className="dim">Nothing has run yet.</p>}
        {runs.length > 0 && (
          <table className="runs">
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} onClick={() => onWatch(r.id)}>
                  <td className="num">{r.id}</td>
                  <td><code>{r.kind}</code></td>
                  <td><StateBadge state={r.state} /></td>
                  <td className="dim">{when(r.startedAt ?? r.queuedAt)} · {took(r)}</td>
                  <td><Counters counters={r.counters} /></td>
                  <td className="ops"><StopRun run={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {anomalies.length > 0 && (
        <section className="panel-section">
          <h2>Data the importer could not resolve <span className="dim">({anomalies.length})</span></h2>
          <p className="dim small">
            Not errors — the catalogue loaded — but something upstream is wrong
            and this is better read than left in a table nobody opens.
          </p>
          <table className="runs">
            <tbody>
              {anomalies.map((a, i) => (
                <tr key={i}>
                  <td><code>{a.kind}</code></td>
                  <td><code>{a.subject}</code></td>
                  <td className="dim small">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function Tile({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="tile">
      <b>{value.toLocaleString()}</b>
      <span>{label}</span>
      {sub && <em>{sub}</em>}
    </div>
  );
}
